import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import styles from "./SearchPage.module.css";
import Wrapper from "./Wrapper";
import Footer from "./Footer";
import { useLanguage } from "../LanguageContext";

const SearchPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [query, setQuery] = useState(searchParams.get("q") || "");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [resultsCount, setResultsCount] = useState(0);
    const inputRef = useRef(null);
    const { language } = useLanguage();

    // Helper function to strip HTML tags and decode HTML entities
    const stripHtml = (html) => {
        if (!html) return "";

        // Create a temporary element to decode HTML entities
        const txt = document.createElement("textarea");
        txt.innerHTML = html;
        const decoded = txt.value;

        // Strip HTML tags
        return decoded.replace(/<[^>]*>/g, "").trim();
    };

    useEffect(() => {
        const queryParam = searchParams.get("q");
        if (queryParam) {
            setQuery(queryParam);
            fetchSearchResults(queryParam);
        }
    }, [searchParams]);

    const generateUrl = (type, item) => {
        switch (type) {
            case "careers":
                return `/careers?id=${item.id}`;
            case "tenders":
                return `/tenders?id=${item.id}`;
            case "news":
                return `/news/${item.id}`;
            case "documents":
                return "/documents";
            case "reports":
                return "/reports";
            default:
                return "/";
        }
    };

    const processSearchResults = (data) => {
        const allResults = [];

        // Process news
        if (data.news && Array.isArray(data.news)) {
            data.news.forEach((item) => {
                allResults.push({
                    type: "news",
                    title: item.title,
                    description: stripHtml(item.description).substring(0, 200),
                    url: generateUrl("news", item),
                    id: item.id,
                });
            });
        }

        // Process reports
        if (data.reports && Array.isArray(data.reports)) {
            data.reports.forEach((item) => {
                allResults.push({
                    type: "reports",
                    title: item.title,
                    description: "",
                    url: generateUrl("reports", item),
                    id: item.id,
                });
            });
        }

        // Process careers
        if (data.careers && Array.isArray(data.careers)) {
            data.careers.forEach((item) => {
                allResults.push({
                    type: "careers",
                    title: item.title,
                    description: stripHtml(item.description),
                    url: generateUrl("careers", item),
                    id: item.id,
                });
            });
        }

        // Process documents
        if (data.documents && Array.isArray(data.documents)) {
            data.documents.forEach((item) => {
                allResults.push({
                    type: "documents",
                    title: item.name,
                    description: "",
                    url: generateUrl("documents", item),
                    id: item.id,
                });
            });
        }

        // Process tenders
        if (data.tenders && Array.isArray(data.tenders)) {
            data.tenders.forEach((item) => {
                allResults.push({
                    type: "tenders",
                    title: item.title,
                    description: stripHtml(item.description),
                    url: generateUrl("tenders", item),
                    id: item.id,
                });
            });
        }

        return allResults;
    };

    const fetchSearchResults = async (searchQuery) => {
        if (!searchQuery.trim()) return;

        setLoading(true);
        console.log("Fetching search results for:", searchQuery);

        try {
            const url = `/backend/search/${`lang?lang=${language === "ENG" ? "en" : "hy"}`}&q=${encodeURIComponent(searchQuery)}`;
            console.log("Fetching from:", url);

            const response = await fetch(url);
            console.log("Response status:", response.status);

            const data = await response.json();
            console.log("Search results:", data);

            const processedResults = processSearchResults(data);
            setResults(processedResults);
            setResultsCount(processedResults.length);
        } catch (error) {
            console.error("Search error:", error);
            setResults([]);
            setResultsCount(0);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        if (e.key === "Enter" && query.trim()) {
            navigate(`/search?q=${encodeURIComponent(query)}`);
        }
    };

    const handleClearSearch = () => {
        setQuery("");
        setResults([]);
        setResultsCount(0);
        navigate("/search");
        inputRef.current?.focus();
    };

    return (
        <Wrapper>
            <div className={styles.searchPage}>
                <div className={styles.searchContainer}>
                    <div className={styles.searchInputWrapper}>
                        <svg className={styles.searchIcon} width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M12.803 12.803C13.4586 12.1473 13.9787 11.369 14.3335 10.5124C14.6883 9.65577 14.8709 8.73765 14.8709 7.81046C14.8709 6.88327 14.6883 5.96515 14.3335 5.10854C13.9787 4.25192 13.4586 3.47359 12.803 2.81796C12.1473 2.16234 11.369 1.64227 10.5124 1.28745C9.65577 0.932624 8.73765 0.75 7.81046 0.75C6.88327 0.75 5.96515 0.932624 5.10854 1.28745C4.25192 1.64227 3.47359 2.16234 2.81796 2.81796C1.49387 4.14205 0.75 5.93791 0.75 7.81046C0.75 9.68301 1.49387 11.4789 2.81796 12.803C4.14205 14.1271 5.93791 14.8709 7.81046 14.8709C9.68301 14.8709 11.4789 14.1271 12.803 12.803ZM12.803 12.803L17.25 17.25"
                                stroke="#8D8D8D"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>

                        <input
                            ref={inputRef}
                            type="text"
                            className={styles.searchInput}
                            placeholder={language === "ARM" ? "Որոնել arca.am" : "Search arca.am"}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={handleSearch}
                        />

                        {query && (
                            <button className={styles.clearButton} onClick={handleClearSearch} aria-label="Clear search">
                                <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M10.5 0C4.65 0 0 4.65 0 10.5C0 16.35 4.65 21 10.5 21C16.35 21 21 16.35 21 10.5C21 4.65 16.35 0 10.5 0ZM14.55 15.75L10.5 11.7L6.45 15.75L5.25 14.55L9.3 10.5L5.25 6.45L6.45 5.25L10.5 9.3L14.55 5.25L15.75 6.45L11.7 10.5L15.75 14.55L14.55 15.75Z"
                                        fill="#8D8D8D"
                                    />
                                </svg>
                            </button>
                        )}
                    </div>
                </div>

                {loading ? (
                    <div className={styles.loaderContainer}>
                        <div className={styles.loader}></div>
                    </div>
                ) : (
                    <>
                        {resultsCount > 0 && (
                            <p className={styles.resultsCount}>
                                {resultsCount} {language === "ARM" ? "արդյունք գտնվել է" : "results found"}
                            </p>
                        )}

                        <div className={styles.resultsContainer}>
                            {results.map((result, index) => (
                                <Link to={result.url} className={styles.resultItemWrapper} key={index}>
                                    <div key={index} className={styles.resultItem}>
                                        <h3 className={styles.resultTitle}>{result.title}</h3>
                                        <p className={styles.resultDescription}>{result.description}</p>
                                        <Link to={result.url} className={styles.resultUrl}>
                                            {result.url}
                                        </Link>
                                    </div>
                                </Link>
                            ))}

                            {results.length === 0 && query && (
                                <p className={styles.noResults}>
                                    {language === "ARM" ? "Արդյունքներ չեն գտնվել" : "No results found for"} "{query}"
                                </p>
                            )}
                        </div>
                    </>
                )}
            </div>
            <Footer />
        </Wrapper>
    );
};

export default SearchPage;
